import { useMutation, useQuery } from '@tanstack/react-query'
import { BookText, Copy, Ticket } from 'lucide-react'
import { parseCookies } from 'nookies'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getCampaignsById } from '@/api/campaigns/get-campaign-by-id'
import {
  getPaymentByPaymentId,
  getPaymentByUsuarioId,
} from '@/api/payments/get-payment'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { env } from '@/env'

interface Payment {
  id: number
  valorUnitario: number
  dataCadastro: string
  campanhaId: number
  usuarioId: number
  titulo: number
  pagamentoId: number
}

interface PaymentDetails {
  externalReference: string
  status: string
  description: string
  paymentMethod: string
  createdAt: string
  expiresAt: string
  qrCode: string
  qrCodeBase64: string
  titulosComprados: string
}

export function CampaignClientView() {
  const { rifaId } = useParams()
  const navigate = useNavigate()
  const { 'reactAuth.id': userId } = parseCookies()
  const [copiedCodes, setCopiedCodes] = useState<Record<number, boolean>>({})
  const [userPayments, setUserPayments] = useState<Payment[]>([])
  const [paymentDetails, setPaymentDetails] = useState<
    Record<number, PaymentDetails>
  >({})

  const {
    data: campaign,
    isLoading,
    isError: campaignError,
  } = useQuery({
    queryKey: ['campaign-client', rifaId],
    queryFn: () => getCampaignsById(Number(rifaId!)),
    enabled: !!rifaId,
  })

  const { mutate: fetchPayments } = useMutation({
    mutationFn: () => getPaymentByUsuarioId(Number(userId)),
    onSuccess: (data) => {
      if (!data) return
      const uniquePayments = data.reduce((acc: Payment[], current: Payment) => {
        const exists = acc.some(
          (payment) => payment.pagamentoId === current.pagamentoId,
        )
        if (!exists) {
          acc.push(current)
        }
        return acc
      }, [])

      setUserPayments(uniquePayments)
    },
    onError: () => toast.error('Erro ao carregar seus pagamentos'),
  })

  useEffect(() => {
    if (userId) fetchPayments()
  }, [userId])

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!rifaId || userPayments.length === 0) return

      const campaignPayments = userPayments.filter(
        (payment) => payment.campanhaId === Number(rifaId),
      )

      const details = await Promise.all(
        campaignPayments.map(async (payment) => {
          try {
            const detail = await getPaymentByPaymentId(payment.pagamentoId)
            return { pagamentoId: payment.pagamentoId, detail }
          } catch {
            return null
          }
        }),
      )

      const detailsMap = details.reduce(
        (acc, curr) => {
          if (curr) acc[curr.pagamentoId] = curr.detail
          return acc
        },
        {} as Record<number, PaymentDetails>,
      )

      setPaymentDetails(detailsMap)
    }

    fetchPaymentDetails()
  }, [userPayments, rifaId])

  const handleCopyCode = (paymentId: number, code: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCodes((prev) => ({ ...prev, [paymentId]: true }))
    setTimeout(() => {
      setCopiedCodes((prev) => ({ ...prev, [paymentId]: false }))
    }, 2000)
    toast.success('Código copiado!')
  }

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-4 p-6">
        <Skeleton className="h-80 w-full rounded-lg" />
        <Skeleton className="h-10 w-1/2" />
      </div>
    )
  }

  if (campaignError || !campaign) {
    return (
      <div className="container mx-auto p-6 text-center">
        <p className="text-red-500">Erro ao carregar a campanha</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    )
  }

  const campaignPayments = [...userPayments]
    .filter((payment) => payment.campanhaId === Number(rifaId))
    .filter((payment) => payment.usuarioId === Number(userId))
    .filter(
      (payment, index, self) =>
        index === self.findIndex((p) => p.pagamentoId === payment.pagamentoId),
    )
    .sort((a, b) => b.pagamentoId - a.pagamentoId)

  return (
    <div className="container mx-auto p-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Campaign Image Section */}
        <div className="col-span-1 flex flex-col items-center lg:items-stretch">
          <Card className="w-full max-w-xs lg:max-w-full">
            <img
              src={`${env.VITE_API_URL}${campaign.campanha.imagem}`}
              alt={campaign.campanha.titulo}
              className="h-full w-full rounded-t-lg object-cover"
              onError={(e) => {
                ;(e.target as HTMLImageElement).src = '/placeholder-image.png'
              }}
            />
            <CardContent className="p-4">
              <h2 className="text-xl font-bold">{campaign.campanha.titulo}</h2>
              <p className="text-muted-foreground mt-2">
                {campaign.campanha.descricao}
              </p>
            </CardContent>
          </Card>

          <Button
            className="mt-4 w-full max-w-xs lg:max-w-full"
            onClick={() => navigate(`/campanhas/${rifaId}/checkout`)}
          >
            Adquirir mais títulos
          </Button>
        </div>

        {/* Campaign Details Section */}
        <div className="col-span-2">
          <Tabs defaultValue="titles">
            <TabsList className="mb-4 grid w-full grid-cols-2 gap-2">
              <TabsTrigger value="details">
                <BookText className="mr-2 h-4 w-4" />
                Detalhes
              </TabsTrigger>
              <TabsTrigger value="titles">
                <Ticket className="mr-2 h-4 w-4" />
                Meus Títulos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <Card>
                <CardHeader className="text-xl font-bold">
                  Informações da Campanha
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-muted-foreground text-sm">
                        Valor do Título
                      </p>
                      <p className="font-medium">
                        R${' '}
                        {campaign.campanha.titulo_Valor
                          ?.toFixed(2)
                          .replace('.', ',')}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">
                        Quantidade Total
                      </p>
                      <p className="font-medium">
                        {campaign.campanha.titulo_Quantidade}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">
                        Data do Sorteio
                      </p>
                      <p className="font-medium">
                        {campaign.campanha.dt_Sorteio
                          ? new Date(
                              campaign.campanha.dt_Sorteio,
                            ).toLocaleDateString()
                          : 'A definir'}
                      </p>
                    </div>
                  </div>

                  {/* Prizes Section */}
                  {campaign.premios.length > 0 && (
                    <div className="pt-4">
                      <h3 className="text-md font-semibold text-green-500">
                        🏆 Prêmios
                      </h3>
                      <ul className="mt-2 space-y-2">
                        {campaign.premios.map((premio) => (
                          <li
                            key={`premio-${premio.id}-${premio.posicao}`}
                            className="flex items-start"
                          >
                            <span className="mr-2 font-medium">
                              {premio.posicao}º
                            </span>
                            <span>{premio.descricao}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Promotions Section - Fixed duplicate key issue */}
                  {campaign.promocoes.length > 0 && (
                    <div className="pt-4">
                      <h3 className="text-md font-semibold text-blue-500">
                        🔥 Promoções
                      </h3>
                      <ul className="mt-2 space-y-2">
                        {campaign.promocoes.map((promo) => (
                          <li
                            key={`promo-${promo.id}-${promo.quantidade}-${promo.valor}`}
                          >
                            <Badge variant="outline" className="mr-2">
                              {promo.quantidade}x
                            </Badge>
                            R$ {promo.valor.toFixed(2).replace('.', ',')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="titles">
              <Card>
                <CardContent>
                  {campaignPayments.length > 0 ? (
                    <Accordion type="multiple" className="w-full">
                      {campaignPayments.map((payment) => {
                        const detail = paymentDetails[payment.pagamentoId]
                        const numbers =
                          detail?.titulosComprados
                            ?.split(',')
                            .filter(Boolean) || []

                        return (
                          <AccordionItem
                            key={`payment-${payment.pagamentoId}`}
                            value={`payment-${payment.pagamentoId}`}
                          >
                            <AccordionTrigger className="hover:no-underline">
                              <div className="flex w-full items-center justify-between gap-4">
                                <div className="grid items-center gap-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">
                                      {numbers.length} título
                                      {numbers.length !== 1 ? 's' : ''}
                                    </Badge>
                                    {detail?.status && (
                                      <Badge
                                        variant={
                                          detail.status === 'approved'
                                            ? 'default'
                                            : 'secondary'
                                        }
                                        className={
                                          detail.status === 'approved'
                                            ? 'bg-green-500 hover:bg-green-600'
                                            : ''
                                        }
                                      >
                                        {detail.status === 'approved'
                                          ? 'Pago'
                                          : detail.status === 'pending'
                                            ? 'Pendente'
                                            : 'Expirado'}
                                      </Badge>
                                    )}

                                    {(() => {
                                      const premio = campaign.premios.find(
                                        (premio) =>
                                          premio.numero_sorteado ===
                                          payment.titulo,
                                      )

                                      return premio ? (
                                        <Badge variant="outline">
                                          <p>{`🎊🎊Parabéns!! Você ganhou o ${premio.descricao}, que é o prêmio da ${premio.posicao}º colocação!!🎊🎊`}</p>
                                        </Badge>
                                      ) : null
                                    })()}

                                    {(() => {
                                      const titulo =
                                        campaign.titulosPremiados.find(
                                          (tituloPremiado) =>
                                            Number(tituloPremiado.titulo) ===
                                            payment.titulo,
                                        )
                                      if (
                                        titulo?.usuario_Ganhador_Id !== null
                                      ) {
                                        return titulo ? (
                                          <Badge variant="outline">
                                            <p>{`🎊🎊Parabéns!! Você ganhou ${titulo.premio}!!🎊🎊`}</p>
                                          </Badge>
                                        ) : null
                                      }
                                    })()}
                                  </div>
                                  <div className="grid items-center gap-2 overflow-hidden pl-3">
                                    <p className="text-sm font-medium">
                                      Compra #{payment.pagamentoId}
                                    </p>
                                    <span className="text-muted-foreground text-xs">
                                      {new Date(
                                        payment.dataCadastro,
                                      ).toLocaleString('pt-BR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </span>

                                    {/* Título truncado */}
                                    <p className="text-muted-foreground truncate text-sm">
                                      {campaign.campanha.titulo}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-4">
                                {/* Purchased Numbers */}
                                <div>
                                  <h4 className="mb-2 font-medium">
                                    Números comprados:
                                  </h4>
                                  <div className="flex flex-wrap gap-2">
                                    {numbers.map((num) => (
                                      <span
                                        key={`num-${payment.id}-${num}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-green-100 font-medium text-green-800"
                                      >
                                        {num}
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                {/* Payment QR Code (show only for pending payments) */}
                                {detail?.status === 'pending' &&
                                  detail?.qrCodeBase64 && (
                                    <div>
                                      <h4 className="mb-2 font-medium">
                                        QR Code para pagamento:
                                      </h4>
                                      <img
                                        src={`data:image/png;base64,${detail.qrCodeBase64}`}
                                        alt="QR Code"
                                        className="mx-auto h-32 w-32"
                                      />
                                    </div>
                                  )}

                                {/* Pix Code */}
                                {detail?.status === 'pending' &&
                                  detail?.qrCode && (
                                    <div>
                                      <h4 className="mb-2 font-medium">
                                        Código Pix:
                                      </h4>
                                      <div className="flex gap-2">
                                        <Input
                                          value={detail.qrCode}
                                          readOnly
                                          className="flex-1 font-mono"
                                        />
                                        <Button
                                          variant="outline"
                                          size="icon"
                                          onClick={() =>
                                            handleCopyCode(
                                              payment.id,
                                              detail.qrCode,
                                            )
                                          }
                                        >
                                          <Copy className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      {copiedCodes[payment.id] && (
                                        <p className="mt-1 text-sm text-green-500">
                                          Código copiado!
                                        </p>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )
                      })}
                    </Accordion>
                  ) : (
                    <div className="py-8 text-center">
                      <Ticket className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                      <p className="text-muted-foreground">
                        Você ainda não adquiriu nenhum título nesta campanha.
                      </p>
                      <Button
                        className="mt-4"
                        onClick={() =>
                          navigate(`/campanhas/${rifaId}/checkout`)
                        }
                      >
                        Adquirir títulos
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
